/**
 * Receitas de medicamentos (Mercado Livre Pharma): fila do farmacêutico. Abre a receita ao lado do pedido e aceita
 * ou recusa com o motivo. Recusa cancela o pedido no Mercado Livre (sem afetar a reputação).
 */
import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, RefreshCw, Search, X } from 'lucide-react';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';

type RxStatus = 'WAITING_VALIDATION' | 'ACCEPTED' | 'REJECTED';
interface RxItem {
  id: string;
  status: RxStatus;
  rejectionReason: string | null;
  rejectionLabel: string | null;
  createdAt: string | null;
  decidedAt: string | null;
  decidedBy: { name: string; crf: string | null } | null;
  decisionSource: 'berytus' | 'channel' | null;
  account: string | null;
  storeId: string | null;
  externalOrderId: string;
  itemId: string | null;
  hasFile: boolean;
  lastError: string | null;
  retentionRecord: RetentionRecord | null;
  retentionRecordedAt: string | null;
  order: { id: string; buyerName: string | null; status: string; createdAt: string | null; packId: string | null; items: { title: string | null; sku: string | null; quantity: number; listing: string | null }[] } | null;
}
interface RetentionRecord { prescriberName?: string; prescriberCouncil?: string; prescriberNumber?: string; prescriberUf?: string; prescriptionDate?: string; retained?: boolean; notes?: string }
interface RxPage { total: number; page: number; pageSize: number; counts: Partial<Record<RxStatus, number>>; canDecide: boolean; canView: boolean; items: RxItem[] }

const TABS: [string, string, RxStatus | null][] = [['pending', 'Pendentes', 'WAITING_VALIDATION'], ['accepted', 'Aceitas', 'ACCEPTED'], ['rejected', 'Recusadas', 'REJECTED'], ['all', 'Todas', null]];
const STATUS: Record<RxStatus, { label: string; tone: string }> = {
  WAITING_VALIDATION: { label: 'Pendente', tone: 'warn' }, ACCEPTED: { label: 'Aceita', tone: 'ok' }, REJECTED: { label: 'Recusada', tone: 'err' },
};

const ago = (iso: string | null) => {
  if (!iso) return '';
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  return min < 60 ? `há ${min} min` : min < 48 * 60 ? `há ${Math.round(min / 60)} h` : `há ${Math.round(min / 1440)} dias`;
};

export function PrescriptionsPage() {
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const qc = useQueryClient();
  const tab = params.get('status') ?? 'pending';
  const page = Number(params.get('page') ?? 1);
  const list = useQuery({
    queryKey: ['prescriptions', tab, page, params.get('q')],
    queryFn: () => api<RxPage>(`/prescriptions?${new URLSearchParams({ status: tab, page: String(page), ...(params.get('q') ? { q: params.get('q')! } : {}) })}`),
    placeholderData: keepPreviousData,
    refetchInterval: tab === 'pending' ? 30_000 : false,
  });
  const sync = useMutation({
    mutationFn: () => api<{ seen: number; downloaded: number; errors: string[] }>('/prescriptions/sync', { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prescriptions'] }),
  });
  const items = list.data?.items ?? [];
  const [openId, setOpenId] = useState<string | null>(null);
  const open = items.find((i) => i.id === openId) ?? items[0] ?? null;
  useEffect(() => { if (openId && !items.some((i) => i.id === openId)) setOpenId(null); }, [items, openId]);

  return (
    <>
      <Head title="Receitas">
        <p>Medicamentos que exigem receita no Mercado Livre: o comprador anexa a receita e o farmacêutico aceita ou recusa antes da etiqueta.
          Recusar cancela o pedido também: a Berytus pede o cancelamento ao Mercado Livre na hora. A Berytus guarda uma cópia de cada receita (o Mercado Livre apaga as aceitas após 2 meses).</p>
      </Head>
      <div className="toolbar">
        <div className="seg" role="group" aria-label="Situação">
          {TABS.map(([v, l, st]) => (
            <button key={v} type="button" aria-pressed={tab === v} onClick={() => { set({ status: v === 'pending' ? null : v, page: null }); setOpenId(null); }}>
              {l}{st && list.data?.counts[st] ? ` (${int(list.data.counts[st]!)})` : ''}
            </button>
          ))}
        </div>
        <label className="search">
          <Search size={16} /><span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="Nº do pedido ou nome do comprador" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <span className="grow" />
        <button className="btn" type="button" disabled={sync.isPending} onClick={() => sync.mutate()}
          title="As receitas novas chegam sozinhas a cada minuto; este botão lê agora.">
          <RefreshCw size={16} /> {sync.isPending ? 'Lendo…' : 'Atualizar agora'}
        </button>
      </div>
      {sync.error && <div style={{ marginBottom: 12 }}><ErrorBox error={sync.error} /></div>}
      {sync.data?.errors.length ? <div className="notice warn small" style={{ marginBottom: 12 }}>{sync.data.errors.join(' · ')}</div> : null}
      {list.data && !list.data.canDecide && (
        <div className="notice small" style={{ marginBottom: 12 }}>
          Só farmacêuticos aceitam ou recusam receitas. O dono da conta marca quem é farmacêutico (com o CRF) em Configurações › Usuários.
        </div>
      )}
      {list.error ? <ErrorBox error={list.error} /> : list.isPending ? <Loading /> : !items.length ? (
        <div className="panel empty"><b>{tab === 'pending' ? 'Nenhuma receita esperando' : 'Nada por aqui'}</b>
          {tab === 'pending' ? 'Quando um comprador anexar uma receita no Mercado Livre, ela aparece aqui em até 1 minuto.' : 'Mude o filtro acima.'}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr', gap: 16, alignItems: 'start' }} className="rx-grid">
          <div className="panel flush" style={{ overflow: 'hidden' }}>
            {items.map((r) => {
              const st = STATUS[r.status];
              const sel = open?.id === r.id;
              return (
                <button key={r.id} type="button" onClick={() => setOpenId(r.id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', border: 0, borderBottom: '1px solid var(--line)', cursor: 'pointer',
                    background: sel ? 'var(--hover)' : 'transparent', color: 'inherit', font: 'inherit' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.order?.items[0]?.title ?? `Pedido ${r.externalOrderId}`}</b>
                    <Tag tone={st.tone}>{st.label}</Tag>
                  </div>
                  <div className="small muted">
                    {list.data!.canView ? r.order?.buyerName ?? 'pedido ainda chegando' : r.order ? 'dados de saúde só para o farmacêutico' : 'pedido ainda chegando'} · {r.status === 'WAITING_VALIDATION' ? ago(r.createdAt) : dateTime(r.decidedAt ?? r.createdAt)}
                  </div>
                </button>
              );
            })}
            <Pager total={list.data!.total} page={page} pageSize={list.data!.pageSize} onPage={(p) => set({ page: p === 1 ? null : String(p) })} />
          </div>
          {open && <Detail key={open.id} rx={open} canDecide={list.data!.canDecide} canView={list.data!.canView} />}
        </div>
      )}
    </>
  );
}

function Detail({ rx, canDecide, canView }: { rx: RxItem; canDecide: boolean; canView: boolean }) {
  const qc = useQueryClient();
  const reasons = useQuery({ queryKey: ['prescription-reasons'], queryFn: () => api<{ code: string; label: string }[]>('/prescriptions/reasons'), staleTime: Infinity });
  const [file, setFile] = useState<{ url: string; type: string } | null>(null);
  const [fileErr, setFileErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (!canView || !rx.hasFile) return;
    let url: string | null = null;
    let alive = true;
    fetch(`/api/v1/prescriptions/${rx.id}/file`, { credentials: 'same-origin' })
      .then(async (res) => {
        if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { message?: string }).message ?? `Erro ${res.status}`);
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        if (alive) setFile({ url, type: blob.type });
      })
      .catch((e: Error) => alive && setFileErr(e.message));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [rx.id, rx.hasFile, canView]);
  const [cancelMsg, setCancelMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const decide = useMutation({
    mutationFn: (b: { action: 'accept' } | { action: 'reject'; reason: string }) =>
      api<{ orderCancel?: { cancelled: boolean; reason: string | null } }>(`/prescriptions/${rx.id}/${b.action}`, { method: 'POST', json: b.action === 'reject' ? { reason: b.reason } : {} }),
    onSuccess: (r) => {
      setRejecting(false);
      if (r?.orderCancel) setCancelMsg(r.orderCancel.cancelled ? { ok: true, text: 'Receita recusada e pedido cancelado no Mercado Livre.' } : { ok: false, text: `Receita recusada, mas o pedido não foi cancelado: ${r.orderCancel.reason}` });
      qc.invalidateQueries({ queryKey: ['prescriptions'] }); qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
  const cancelOrder = useMutation({
    mutationFn: () => api(`/prescriptions/${rx.id}/cancel-order`, { method: 'POST', json: {} }),
    onSuccess: () => { setCancelMsg({ ok: true, text: 'Pedido cancelado no Mercado Livre.' }); qc.invalidateQueries({ queryKey: ['prescriptions'] }); qc.invalidateQueries({ queryKey: ['orders'] }); },
  });
  const orderOpen = rx.order && !['cancelled', 'shipped', 'delivered'].includes(rx.order.status);
  const pending = rx.status === 'WAITING_VALIDATION';
  return (
    <div className="panel" style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0 }}>Pedido {rx.externalOrderId}</h2>
          <div className="small muted">
            {canView ? rx.order?.buyerName ?? 'Comprador ainda não carregado' : 'Paciente e medicamentos: só para o farmacêutico (CRF)'} · receita enviada {dateTime(rx.createdAt)} ({ago(rx.createdAt)})
            {rx.account ? ` · ${rx.account}` : ''}{rx.storeId ? ` · filial ${rx.storeId}` : ''}
          </div>
        </div>
        <Tag tone={STATUS[rx.status].tone}>{STATUS[rx.status].label}</Tag>
      </div>

      <div>
        <div className="small muted" style={{ marginBottom: 4 }}>Medicamentos do pedido — confira com a receita</div>
        {!canView && rx.order ? <div className="small muted">Os medicamentos e a receita ficam só para o farmacêutico (dados de saúde, LGPD).</div> : rx.order?.items.length ? (
          <table className="simple">
            <thead><tr><th>Produto</th><th>SKU</th><th className="num">Qtd.</th></tr></thead>
            <tbody>{rx.order.items.map((i, k) => <tr key={k}><td>{i.title}</td><td>{i.sku ?? '—'}</td><td className="num"><b>{int(i.quantity)}</b></td></tr>)}</tbody>
          </table>
        ) : <div className="small muted">O pedido ainda não chegou à Berytus (item {rx.itemId ?? '—'}). Ele é buscado automaticamente.</div>}
      </div>

      {cancelMsg && <div className={cancelMsg.ok ? 'notice small' : 'notice small warn'} style={cancelMsg.ok ? undefined : { color: 'var(--err)' }}>{cancelMsg.text}</div>}
      {rx.status === 'REJECTED' && orderOpen && (canDecide || canView) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: 10 }}>
          <span className="small"><b>Receita recusada, mas o pedido continua ativo.</b> Cancele para ele não ser separado nem enviado.</span>
          <button className="btn danger" type="button" disabled={cancelOrder.isPending} onClick={() => cancelOrder.mutate()}>{cancelOrder.isPending ? 'Cancelando…' : 'Cancelar o pedido'}</button>
          {cancelOrder.error && <ErrorBox error={cancelOrder.error} />}
        </div>
      )}
      {pending && canDecide && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: 10 }}>
          {!rejecting ? (
            <>
              <button className="btn primary" type="button" disabled={decide.isPending || !rx.hasFile} onClick={() => decide.mutate({ action: 'accept' })}>
                <Check size={16} /> {decide.isPending ? 'Enviando…' : 'Aceitar receita'}
              </button>
              <button className="btn danger" type="button" disabled={decide.isPending} onClick={() => setRejecting(true)}><X size={16} /> Recusar</button>
              <span className="small muted">Aceitar libera a etiqueta e o envio do pedido.</span>
            </>
          ) : (
            <>
              <select className="select" style={{ maxWidth: 380 }} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Motivo da recusa" autoFocus>
                <option value="">Motivo da recusa…</option>
                {reasons.data?.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
              <button className="btn danger" type="button" disabled={!reason || decide.isPending} onClick={() => decide.mutate({ action: 'reject', reason })}>
                {decide.isPending ? 'Enviando…' : 'Recusar e cancelar o pedido'}
              </button>
              <button className="btn" type="button" onClick={() => setRejecting(false)}>Voltar</button>
            </>
          )}
        </div>
      )}
      {decide.error && <ErrorBox error={decide.error} />}
      {!canView ? (
        <div className="notice small">Só farmacêuticos e administradores veem a receita.</div>
      ) : !rx.hasFile ? (
        <div className="notice warn small">{rx.lastError ?? 'A cópia da receita está sendo baixada do Mercado Livre (até 1 minuto).'}</div>
      ) : fileErr ? <div className="notice err small">{fileErr}</div> : !file ? <Loading /> : file.type.startsWith('image/') ? (
        <img src={file.url} alt="Receita" style={{ maxWidth: '100%', border: '1px solid var(--line)', borderRadius: 8 }} />
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          <iframe src={file.url} title="Receita" style={{ width: '100%', height: '70vh', border: '1px solid var(--line)', borderRadius: 8, background: '#fff' }} />
          <a className="small" href={file.url} download={`receita-${rx.externalOrderId}.pdf`}>Baixar a receita</a>
        </div>
      )}

      {canDecide && <RetentionForm rx={rx} />}
      {!pending && (
        <div className="small muted">
          {rx.status === 'ACCEPTED' ? 'Aceita' : `Recusada (${rx.rejectionLabel ?? rx.rejectionReason ?? 'sem motivo'})`} {dateTime(rx.decidedAt)}
          {rx.decidedBy ? ` por ${rx.decidedBy.name}${rx.decidedBy.crf ? ` (CRF ${rx.decidedBy.crf})` : ''}` : rx.decisionSource === 'channel' ? ' no painel do Mercado Livre' : ''}
        </div>
      )}
    </div>
  );
}

/** Registro de retenção (A5): dado adicional do farmacêutico, nunca bloqueia aceitar, recusar, nota ou etiqueta. */
function RetentionForm({ rx }: { rx: RxItem }) {
  const qc = useQueryClient();
  const [f, setF] = useState<RetentionRecord>(rx.retentionRecord ?? {});
  const save = useMutation({ mutationFn: () => api(`/prescriptions/${rx.id}/record`, { method: 'PUT', json: f }), onSuccess: () => qc.invalidateQueries({ queryKey: ['prescriptions'] }) });
  const set = (k: keyof RetentionRecord, v: string | boolean) => setF({ ...f, [k]: v === '' ? undefined : v });
  return (
    <details className="small">
      <summary>Registro de retenção {rx.retentionRecordedAt ? `(gravado ${dateTime(rx.retentionRecordedAt)})` : '(opcional)'}</summary>
      <p className="muted" style={{ margin: '4px 0' }}>Só para o seu controle: não bloqueia aceitar, recusar, nota nem etiqueta.</p>
      <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <label>Prescritor<input className="input" value={f.prescriberName ?? ''} onChange={(e) => set('prescriberName', e.target.value)} /></label>
        <label>Conselho<select className="select" value={f.prescriberCouncil ?? ''} onChange={(e) => set('prescriberCouncil', e.target.value)}>
          <option value="">—</option>{['CRM', 'CRO', 'CRMV', 'COREN', 'outro'].map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
        <label>Número<input className="input" value={f.prescriberNumber ?? ''} onChange={(e) => set('prescriberNumber', e.target.value)} /></label>
        <label>UF<input className="input" maxLength={2} value={f.prescriberUf ?? ''} onChange={(e) => set('prescriberUf', e.target.value.toUpperCase())} /></label>
        <label>Data da receita<input className="input" type="date" value={f.prescriptionDate ?? ''} onChange={(e) => set('prescriptionDate', e.target.value)} /></label>
        <label style={{ alignSelf: 'end' }}><input type="checkbox" checked={!!f.retained} onChange={(e) => set('retained', e.target.checked)} /> Receita retida</label>
      </div>
      <label style={{ display: 'block', marginTop: 6 }}>Observação<input className="input" value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value)} /></label>
      <button type="button" className="btn sm" style={{ marginTop: 6 }} disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Gravando…' : 'Gravar registro'}</button>
      {save.error && <ErrorBox error={save.error} />}
    </details>
  );
}
